import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wrench, Calendar, User, Package, Plus, Trash2, Tag } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch (e) { return iso; }
}

export default function Maintenance() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [query, setQuery] = useState("");
  const [newItv, setNewItv] = useState("");

  const load = async () => {
    const r = await api.get("/maintenance");
    setRecords(r.data);
    const i = await api.get("/interventions");
    setInterventions(i.data);
  };
  useEffect(() => { load(); }, []);

  const addCustom = async () => {
    if (!newItv.trim()) return;
    try {
      await api.post("/interventions", { nome: newItv.trim() });
      setNewItv(""); toast.success("Intervento aggiunto"); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const delItv = async (itv) => {
    try { await api.delete(`/interventions/${itv.id}`); toast.success("Eliminato"); await load(); }
    catch (e) { toast.error("Errore"); }
  };
  const putOOS = async (r) => {
    try {
      await api.post("/indisponibilita", {
        bike_id: r.bike_id,
        data_inizio: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
        data_fine: null, motivo: "manutenzione",
        note: `Da intervento del ${fmtDate(r.data)}`,
      });
      toast.success(`Bici ${r.bike_numero} messa fuori servizio (vedi Calendario per impostare la fine)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const filtered = records.filter((r) =>
    [r.bike_numero, r.eseguito_da, (r.intervention_names || []).join(" "), r.descrizione].join(" ").toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto fade-in">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Registro interventi</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">MANUTENZIONE</h1>
      </div>

      <Tabs defaultValue="registro">
        <TabsList className="bg-[#141414] border border-zinc-800 rounded-sm mb-6">
          <TabsTrigger value="registro" data-testid="tab-registro" className="data-[state=active]:bg-[#f98125] data-[state=active]:text-white rounded-sm">Registro</TabsTrigger>
          <TabsTrigger value="tipi" data-testid="tab-tipi" className="data-[state=active]:bg-[#f98125] data-[state=active]:text-white rounded-sm">Tipi intervento</TabsTrigger>
        </TabsList>

        <TabsContent value="registro">
          <div className="relative mb-6 max-w-md">
            <Input data-testid="maintenance-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per bici, intervento, esecutore..." className="bg-[#0A0A0A] border-zinc-800 rounded-sm" />
          </div>
          {filtered.length === 0 ? (
            <div className="text-zinc-600 text-sm py-16 text-center border border-dashed border-zinc-800 rounded-sm">Nessun intervento registrato.</div>
          ) : (
            <div className="space-y-3">
              {filtered.map((r) => (
                <div key={r.id} data-testid={`global-maintenance-${r.id}`}
                  className="bg-[#141414] border border-zinc-800 rounded-sm p-4 hover:border-[#f98125]/40 transition-colors">
                  <div className="flex items-start gap-4">
                    <button onClick={() => navigate(`/bici/${r.bike_id}`)}
                      className="h-12 w-12 shrink-0 bg-[#f98125]/15 rounded-sm flex items-center justify-center hover:bg-[#f98125]/30 transition-colors">
                      <span className="font-display text-2xl text-[#f98125]">{r.bike_numero}</span>
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap gap-1.5">
                        {(r.intervention_names || []).map((n, i) => (
                          <span key={i} className="text-xs bg-[#f98125]/15 text-[#f98125] px-2 py-1 rounded-sm">{n}</span>
                        ))}
                      </div>
                      {r.descrizione && <p className="text-sm text-zinc-300 mt-2">{r.descrizione}</p>}
                      {(r.parts_used || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {r.parts_used.map((p, i) => (
                            <span key={i} className="text-[11px] text-zinc-400 bg-zinc-800/60 px-2 py-0.5 rounded-sm flex items-center gap-1"><Package className="h-3 w-3" /> {p.nome} ×{p.quantita}</span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(r.data)}</span>
                        <span className="flex items-center gap-1"><User className="h-3 w-3" /> {r.eseguito_da}</span>
                        {hasPerm("maintenance.create") && (
                          <button onClick={() => putOOS(r)} data-testid={`oos-from-maint-${r.id}`} className="flex items-center gap-1 text-violet-400 hover:text-violet-300"><Wrench className="h-3 w-3" /> Fuori servizio</button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="tipi">
          {hasPerm("interventions.manage") && (
            <div className="flex gap-2 mb-6 max-w-md">
              <Input value={newItv} onChange={(e) => setNewItv(e.target.value)} data-testid="new-itv-input"
                placeholder="Nuovo tipo di intervento..." className="bg-[#0A0A0A] border-zinc-800 rounded-sm"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} />
              <button onClick={addCustom} data-testid="add-itv-btn" className="flex items-center gap-1 px-4 rounded-sm bg-[#f98125] text-white font-bold hover:bg-[#d96a12]"><Plus className="h-4 w-4" /> Aggiungi</button>
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {interventions.map((itv) => (
              <div key={itv.id} data-testid={`itv-item-${itv.id}`} className="flex items-center justify-between bg-[#141414] border border-zinc-800 rounded-sm px-4 py-3">
                <span className="flex items-center gap-2 text-sm"><Tag className="h-4 w-4 text-zinc-600" /> {itv.nome}</span>
                <div className="flex items-center gap-2">
                  {itv.custom && <span className="text-[10px] uppercase tracking-wider text-[#39FF14]">custom</span>}
                  {hasPerm("interventions.manage") && (
                    <button onClick={() => delItv(itv)} data-testid={`del-itv-${itv.id}`} className="p-1 text-zinc-600 hover:text-[#FF3B30]"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
